"use client"

/* eslint-disable no-magic-numbers */
/* eslint-disable */
import React, { useContext, useEffect, useMemo, useState } from "react"
import { FormattedMessage } from "react-intl"

import Tooltip from "components/Tooltip"
import InfoIcon from "assets/icons/info-icon"
import Row from "components/Row"
import Container from "components/Container"
import Col from "components/Col"
import SearchDropdown from "components/SearchDropdown"
import Modal from "components/Modal"
import { requestIUCNListSearch } from "api/iucnList"
import {
  requestCreateSiteSpecie,
  requestDeleteSiteSpecie,
  requestGetAllSiteSpecies,
  requestGetSiteSpecies,
} from "api/siteSpecie"
import {
  requestUpdateSpeciesFiles,
  requestDeleteSpeciesFiles,
  requestGetSpeciesFiles, // supports { id, siteHistoryId } or positional
} from "api/site"

import { delay } from "utils/delay"
import { SiteContext } from "providers/site"
import ItemsPerPageDropdown from "components/ItemsPerPageDropdown"
import Pagination from "features/Pagination"
import LoadingWrapper from "components/LoadingWrapper"
import { useParams } from "react-router-dom"
import { showErrorToast } from "utils/toast"
import { useMultiplePagination } from "hooks/useMultiplePagination"

import {
  AssessmentPageSubTittle,
  AssessmentPageSubTittleIconWrapper,
  AssessmentPageSubTittleWrapper,
  AssessmentPageTittle,
  AssessmentPageTittleWrapper,
} from "../../style"
import { SpeciesDetailsSearchFormWrapper, SpeciesPaginationWrapper, SpeciesSearchContainer } from "./style"
import RemoveSpeciesModal from "./RemoveSpeciesModal"
import SpeciesDetailsModal from "./SpeciesDetailsModal/SpeciesDetailsModal"
import { defaultSiteProposalValidationResults } from "constants/defaultSiteProposalValidationResults"
import {
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID,
  SITE_STATUS_CONFIRMED_ID,
} from "constants/siteStatuses"
import SpeciesConfirmDeclineList from "./SpeciesConfirmDeclineList/SpeciesConfirmDeclineList"
import { SpeciesListTitle, SpeciesListTitleWrapper } from "./SpeciesConfirmDeclineList/style"
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID } from "constants/roles"
import { ProfileContext } from "providers/profile"
import Uploader from "flow/SiteProposal/pages/About/uploader"
import UploadSiteFiles from "components/SitesUploads"

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 12,
  totalPages: 1,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false,
}

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const updateSearchOptionsTimeOut = delay(inputSaveDelayTime)

function resolveHistoryId(editOnly, site, params) {
  const fromParams =
    params?.siteHistoryId ??
    params?.sitehistoryId ??
    params?.historyId ??
    params?.HistoryId ??
    params?.history_id

  const fromSiteFlat =
    site?.sitehistoryId ??
    site?.siteHistoryId ??
    site?.SiteHistoryId ??
    site?.historyId ??
    site?.HistoryId ??
    site?.history_id ??
    site?.currentSiteHistoryId ??
    site?.currentHistoryId

  const fromSiteNested =
    site?.SiteHistory?.id ??
    site?.siteHistory?.id ??
    site?.history?.id ??
    site?.History?.id

  return editOnly ? (fromSiteFlat ?? fromParams ?? fromSiteNested) : (fromParams ?? fromSiteFlat ?? fromSiteNested)
}

function resolveSiteId(editOnly, site, params) {
  const fromParams = params?.siteId ?? params?.id ?? params?.SiteId
  const fromSite = site?.siteId ?? site?.id ?? site?.SiteId
  return editOnly ? (fromSite ?? fromParams) : (fromParams ?? fromSite)
}

const SpeciesDetailsSearchForm = ({ site }) => {
  const [species, setSpecies] = useState(null)
  const [speciesList, setSpeciesList] = useState([])
  const [newSpecies, setNewSpecies] = useState([])
  const [oldSpecies, setOldSpecies] = useState([])
  const [rejectedSpecies, setRejectedSpecies] = useState([])
  const [searchOptions, setSearchOptions] = useState([])
  const [scientificName, setScientificName] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSpeciesDetailsModalOpen, setIsSpeciesDetailsModalOpen] = useState(false)
  const [speciesToWorkWith, setSpeciesToWorkWith] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const params = useParams()
  const { siteProposalFieldValidation, setSiteProposalFieldValidation, isEditable, setSite } = useContext(SiteContext)

  const [hasSpecies, setHasSpecies] = useState(false)

  const disabled =
    site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)
  const editOnlySiteStatus =
    site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID

  const historyId = useMemo(() => resolveHistoryId(editOnlySiteStatus, site, params), [editOnlySiteStatus, site, params])
  const siteId = useMemo(() => resolveSiteId(editOnlySiteStatus, site, params), [editOnlySiteStatus, site, params])
  const isReassessment = Boolean(historyId)

  const { allSpeciesPagination, oldSpeciesPagination, newSpeciesPagination } =
    useMultiplePagination(defaultPaginationState)

  const { profile } = useContext(ProfileContext)
  const isNcgOrIndividualUser = profile.Role.id === NCG_ROLE_ID || profile.Role.id === INDIVIDUAL_ROLE_ID

  const [speciesDocs, setSpeciesDocs] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [isFilesLoading, setIsFilesLoading] = useState(false)

  const isConfirmed = site.SiteStatusId === SITE_STATUS_CONFIRMED_ID
  const showUploader = !isConfirmed && hasSpecies
  const uploaderDisabled = isConfirmed || !hasSpecies

  // ---------- file helpers & cache key ----------
  function normFile(f) {
    const n = { ...f }
    n.id = n.id ?? n.file_id ?? null
    n.name = n.name ?? n.file_name ?? n.originalFilename ?? n.originalName ?? ""
    n.blob_name = n.blob_name ?? n.blobName ?? n.id ?? n.file_id ?? ""

    // Prefer the SAS URL from backend for downloads; use as-is (no re-encoding/rebuilding)
    n.fileUrl = n.fileUrl ?? n.url ?? n.sasUrl ?? n.download_url ?? ""

    // Bare/public URL (only if your container allows it)
    const blob = n.blob_name ?? n.blobName
    n.bareUrl = n.bareUrl ?? (blob ? `https://wdkbasitesfiles.blob.core.windows.net/sites-files/${blob}` : "")

    // Expose both `url` and `downloadUrl` to consumers; SAS first
    n.url = n.fileUrl || n.bareUrl || ""
    n.downloadUrl = n.fileUrl || n.url

    n.size = n.size ?? null
    n.mime = n.mime ?? n.mimetype ?? n.contentType ?? ""
    n.fileExtension = n.fileExtension ?? n.extension ?? null
    n.uploadedBy = n.uploadedBy ?? null
    return n
  }

  function uniqByKey(arr) {
    const seen = new Set()
    return (arr || []).filter((x) => {
      const n = normFile(x)
      const k = n.blob_name || n.url || n.id || ""
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }

  // Species files are managed separately from site context

  // derive hasSpecies
  useEffect(() => {
    const totalSpecies = speciesList.length + newSpecies.length + oldSpecies.length + rejectedSpecies.length
    setHasSpecies(totalSpecies > 0)
  }, [speciesList.length, newSpecies.length, oldSpecies.length, rejectedSpecies.length])

  function getSpecies(element) {
    const maybePromise = requestIUCNListSearch && requestIUCNListSearch(element)
    if (!maybePromise || typeof maybePromise.then !== "function") return
    maybePromise.then((data) => {
      if (data.length > 0) {
        const result = data.map((item, index) => {
          return { label: item.scientific_name, value: index, taxonid: item.taxonid }
        })
        setSearchOptions(result)
      } else {
        setSearchOptions([])
      }
    })
  }

  function getSiteSpecies() {
    setIsLoading(true)
    requestGetAllSiteSpecies(siteId, historyId, {
      resultsPerPage: allSpeciesPagination.pagination.resultsPerPage,
      currentPage: allSpeciesPagination.pagination.currentPage,
    }).then((data) => {
      setIsLoading(false)
      setSpeciesList([...(data?.species || [])])
      allSpeciesPagination.setPagination(data?.pagination || {})
    })
    requestGetAllSiteSpecies(siteId, historyId, {
      resultsPerPage: allSpeciesPagination.pagination.totalNumber,
      currentPage: 1,
    }).then((data) => {
      setIsLoading(false)
      setRejectedSpecies([...(data?.species || [])])
    })
  }

  function getNewSiteSpecies() {
    setIsLoading(true)
    requestGetSiteSpecies(
      siteId,
      historyId,
      {
        resultsPerPage: newSpeciesPagination.pagination.resultsPerPage,
        currentPage: newSpeciesPagination.pagination.currentPage,
      },
      true,
    ).then((data) => {
      setIsLoading(false)
      setNewSpecies([...(data?.species || [])])
      newSpeciesPagination.setPagination(data?.pagination || {})
    })
  }

  function getOldSiteSpecies() {
    setIsLoading(true)
    requestGetSiteSpecies(
      siteId,
      historyId,
      {
        resultsPerPage: oldSpeciesPagination.pagination.resultsPerPage,
        currentPage: oldSpeciesPagination.pagination.currentPage,
      },
      false,
    ).then((data) => {
      setIsLoading(false)
      setOldSpecies([...(data?.species || [])])
      oldSpeciesPagination.setPagination(data?.pagination || {})
    })
  }

  function addSpeciesToSite(element) {
    const taxonId = element?.taxonid
    if (!taxonId || typeof taxonId === "object") {
      showErrorToast(<FormattedMessage id="invalidTaxonid" />)
      return
    }

    requestCreateSiteSpecie(siteId, taxonId.toString(), params.siteHistoryId).then(
      (data) => {
        if (data?.error) {
          if (typeof data.error === "string") {
            return showErrorToast(<FormattedMessage id={data.error} />)
          }
          return
        }
        setSpeciesToWorkWith(data)
        setIsSpeciesDetailsModalOpen(true)
        getSiteSpecies()
        getNewSiteSpecies()
        setSiteProposalFieldValidation({
          ...siteProposalFieldValidation,
          species: defaultSiteProposalValidationResults.species,
          updated: false,
        })
      },
    )
  }

  function openCancelModal() {
    setIsOpen(true)
  }

  function handleInputChange(e) {
    setScientificName(e)
    if (e.length > 4) {
      updateSearchOptionsTimeOut(() => getSpecies(e))
    } else {
      setSearchOptions([])
    }
  }

  function handleOpenEditModal(e) {
    setSpeciesToWorkWith(e)
    setIsSpeciesDetailsModalOpen(true)
  }

  function removeSpeciesFromList(id, historyIdParam) {
    requestDeleteSiteSpecie(id, params.siteHistoryId, historyIdParam).then(() => {
      getSiteSpecies()
      getNewSiteSpecies()
      getOldSiteSpecies()
    })
  }

  function openRemovalModal(specie) {
    setSpeciesToWorkWith(specie)
    setIsModalOpen(true)
  }

  function closeEditSpeciesModal() {
    const taxonTreatmentsToShowWarning = ["A-NR", "Q-NR", "S-NR", "U-NR", "A-UR"]
    const hasTaxonTreatment = speciesToWorkWith && speciesToWorkWith.taxonTreatment
    if (!hasTaxonTreatment || !taxonTreatmentsToShowWarning.includes(speciesToWorkWith.taxonTreatment)) {
      setSpeciesToWorkWith(false)
      setIsSpeciesDetailsModalOpen(false)
      getSiteSpecies()
      getNewSiteSpecies()
      getOldSiteSpecies()
    }
  }

  useEffect(() => {
    function handleSensitiveSpeciesAcknowledged(event) {
      const { siteId: acknowledgedSiteId, siteHistoryId: acknowledgedHistoryId } = event.detail || {}
      if (!acknowledgedSiteId || acknowledgedSiteId.toString() !== String(siteId)) return
      if (acknowledgedHistoryId && String(acknowledgedHistoryId) !== String(historyId)) return
      getSiteSpecies()
      getNewSiteSpecies()
      getOldSiteSpecies()
    }

    window.addEventListener('sensitiveSpeciesAcknowledged', handleSensitiveSpeciesAcknowledged)

    return () => {
      window.removeEventListener('sensitiveSpeciesAcknowledged', handleSensitiveSpeciesAcknowledged)
    }
  }, [siteId, historyId])

  // extract files across possible API shapes
  function extractFiles(res) {
    if (!res) return []
    if (Array.isArray(res)) return res
    if (Array.isArray(res.files)) return res.files
    if (Array.isArray(res.data?.files)) return res.data.files
    if (Array.isArray(res.data)) return res.data
    if (Array.isArray(res.results)) return res.results
    return []
  }


  // fetch from server (assessment or reassessment)
  async function loadSpeciesFiles() {
    if (!siteId) return
    try {
      setIsFilesLoading(true)
      let res
      if (isReassessment) {
        try {
          res = await requestGetSpeciesFiles({ id: siteId, siteHistoryId: historyId })
        } catch {
          res = await requestGetSpeciesFiles(siteId, historyId)
        }
      } else {
        try {
          res = await requestGetSpeciesFiles({ id: siteId })
        } catch {
          res = await requestGetSpeciesFiles(siteId)
        }
      }

      const serverFiles = extractFiles(res).map(normFile)

      // merge with cache
      let cached = []
      const merged = uniqByKey([...(cached || []), ...serverFiles])

      setUploadedFiles(merged)
      // Don't update site context with species files - they should be separate

    } catch {
      showErrorToast(<FormattedMessage id="loadFilesFailed" defaultMessage="Failed to load files" />)
    } finally {
      setIsFilesLoading(false)
    }
  }

  const renderSpeciesListToConfirmApprovedBySecretariat = speciesList
    .filter((item) => item.isApprovedBySecretariat)
    .map((item, index) => {
      const qualified = item.lastReassessmentDate
        ? item.triggerReasonId.some((element) => element === 1)
        : true
      return (
        <SpeciesConfirmDeclineList
          getSiteSpecies={getSiteSpecies}
          site={site}
          item={item}
          lastItem={index + 1 === speciesList.length}
          key={index}
          permitDelete={site.SiteStatusId === SITE_STATUS_DRAFT_ID}
          nonQualified={!qualified}
          onClick={handleOpenEditModal}
          openRemovalModal={openRemovalModal}
        />
      )
    })

  const renderSpeciesListToConfirmRejectedBySecretariat = rejectedSpecies
    .filter((item) => !item.isApprovedBySecretariat)
    .map((item, index) => {
      const qualified = item.lastReassessmentDate
        ? item.triggerReasonId.some((element) => element === 1)
        : true
      return (
        <SpeciesConfirmDeclineList
          getSiteSpecies={getSiteSpecies}
          site={site}
          item={item}
          lastItem={index + 1 === speciesList.length}
          key={index}
          nonQualified={!qualified}
          onClick={handleOpenEditModal}
          openRemovalModal={openRemovalModal}
        />
      )
    })

  const renderSpeciesListNewAssessment = newSpecies.map((item, index) => (
    <SpeciesConfirmDeclineList
      getSiteSpecies={getNewSiteSpecies}
      site={site}
      item={item}
      lastItem={index + 1 === newSpecies.length}
      key={index}
      onClick={handleOpenEditModal}
      permitDelete={true}
      openRemovalModal={openRemovalModal}
    />
  ))

  const renderSpeciesPreviousAssessment = oldSpecies
    .filter((item) => item.lastReassessmentDate)
    .map((item, index) => {
      const qualified = item.lastReassessmentDate
        ? item.triggerReasonId.some((element) => element === 1)
        : true
      return (
        <SpeciesConfirmDeclineList
          getSiteSpecies={getOldSiteSpecies}
          site={site}
          item={item}
          lastItem={index + 1 === oldSpecies.length}
          key={index}
          nonQualified={!qualified}
          onClick={handleOpenEditModal}
          openRemovalModal={openRemovalModal}
        />
      )
    })

  useEffect(() => {
    getNewSiteSpecies()
  }, [newSpeciesPagination.pagination?.currentPage, newSpeciesPagination.pagination?.resultsPerPage])

  useEffect(() => {
    getOldSiteSpecies()
  }, [oldSpeciesPagination.pagination?.currentPage, oldSpeciesPagination.pagination?.resultsPerPage])

  useEffect(() => {
    getSiteSpecies()
  }, [allSpeciesPagination.pagination?.currentPage, allSpeciesPagination.pagination?.resultsPerPage])


  // initial / id changes
  useEffect(() => {
    if (siteId) {
      loadSpeciesFiles()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, historyId])

  // Build uploader params
  const uploaderParams = useMemo(() => {
    const base = { id: site.id }
    if (historyId) base.siteHistoryId = historyId
    return base
  }, [site.id, historyId])

  // Make sure Uploader gets SAS-first URLs
  const uploaderFiles = useMemo(() => (uploadedFiles || []).map((f) => normFile(f)), [uploadedFiles])

  return (
    <SpeciesDetailsSearchFormWrapper>
      <Container>
        <Row>
          <Col md={8} mdOffset={2}>
            <AssessmentPageTittleWrapper firstTitle={true}>
              <AssessmentPageTittle data-test="legend">
                <FormattedMessage id="speciesDetails" />
              </AssessmentPageTittle>
              <AssessmentPageSubTittleWrapper marginBottom="48px">
                <AssessmentPageSubTittle>
                  <FormattedMessage id="speciesDetailsSubTittle" />
                </AssessmentPageSubTittle>
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement="bottom"
                  trigger={["hover"]}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: "speciesDetailSubTittle", infoTooltip: "speciesDetailSubTittleHint" }}
                  link={"info@keybiodiversityareas.org"}
                >
                  <AssessmentPageSubTittleIconWrapper>
                    <InfoIcon />
                  </AssessmentPageSubTittleIconWrapper>
                </Tooltip>
              </AssessmentPageSubTittleWrapper>
            </AssessmentPageTittleWrapper>
          </Col>
        </Row>
        {site.SiteStatusId !== SITE_STATUS_NOMINATED_ID ? (
          <>
            <Row>
              <Col md={8} mdOffset={2}>
                <SpeciesSearchContainer disabled={disabled}>
                  <SearchDropdown
                    value={species}
                    onChange={setSpecies}
                    options={searchOptions}
                    placeholder="searchByScientificName"
                    field="speciesDetails"
                    buttonName="addSpecies"
                    buttonDisabled={!species}
                    height="40px"
                    marginBottom="0"
                    marginTop="0"
                    menuWidth="416px"
                    buttonOnClick={addSpeciesToSite}
                    inputValue={scientificName}
                    onInputChange={handleInputChange}
                    disabled={disabled}
                  />
                </SpeciesSearchContainer>
              </Col>
            </Row>
            {renderSpeciesListNewAssessment.length > 0 && (
              <>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle>
                        <FormattedMessage id="newTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col md={8} mdOffset={2} dataTest="new-added-species">
                    <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                      {renderSpeciesListNewAssessment}
                      <SpeciesPaginationWrapper>
                        <ItemsPerPageDropdown
                          currentPage={newSpeciesPagination.pagination.currentPage}
                          resultsPerPage={newSpeciesPagination.pagination.resultsPerPage}
                          totalNumber={newSpeciesPagination.pagination.totalNumber}
                          onChange={newSpeciesPagination.changeResultsPerPage}
                          dataTest="sites-items-per-page"
                        />
                        {newSpeciesPagination.pagination.totalPages > 1 && (
                          <Pagination
                            activePages={newSpeciesPagination.pagination.currentPage}
                            numberOfPages={newSpeciesPagination.pagination.totalPages}
                            nextPage={newSpeciesPagination.nextPage}
                            prevPage={newSpeciesPagination.prevPage}
                            nextSection={newSpeciesPagination.nextSection}
                            prevSection={newSpeciesPagination.prevSection}
                            selectPage={newSpeciesPagination.selectPage}
                            dataTest="page-number"
                          />
                        )}
                      </SpeciesPaginationWrapper>
                    </LoadingWrapper>
                  </Col>
                </Row>
              </>
            )}
            {renderSpeciesPreviousAssessment.length > 0 && (
              <>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle marginTop={renderSpeciesListNewAssessment.length > 0 ? "48px" : "0"}>
                        <FormattedMessage id="existingTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <Row>
                  <Col md={8} mdOffset={2} dataTest="existing-species">
                    <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                      {renderSpeciesPreviousAssessment}
                      <SpeciesPaginationWrapper>
                        <ItemsPerPageDropdown
                          currentPage={oldSpeciesPagination.pagination.currentPage}
                          resultsPerPage={oldSpeciesPagination.pagination.resultsPerPage}
                          totalNumber={oldSpeciesPagination.pagination.totalNumber}
                          onChange={oldSpeciesPagination.changeResultsPerPage}
                          dataTest="sites-items-per-page"
                        />
                        {oldSpeciesPagination.pagination.totalPages > 1 && (
                          <Pagination
                            activePages={oldSpeciesPagination.pagination.currentPage}
                            numberOfPages={oldSpeciesPagination.pagination.totalPages}
                            nextPage={oldSpeciesPagination.nextPage}
                            prevPage={oldSpeciesPagination.prevPage}
                            nextSection={oldSpeciesPagination.nextSection}
                            prevSection={oldSpeciesPagination.prevSection}
                            selectPage={oldSpeciesPagination.selectPage}
                            dataTest="page-number"
                          />
                        )}
                      </SpeciesPaginationWrapper>
                    </LoadingWrapper>
                  </Col>
                </Row>
              </>
            )}
          </>
        ) : (
          <Row>
            {renderSpeciesListToConfirmApprovedBySecretariat.length > 0 && (
              <Col md={8} mdOffset={2}>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle marginTop="0">
                        <FormattedMessage id="approvedTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                  {renderSpeciesListToConfirmApprovedBySecretariat}
                  <SpeciesPaginationWrapper>
                    <ItemsPerPageDropdown
                      currentPage={allSpeciesPagination.pagination.currentPage}
                      resultsPerPage={allSpeciesPagination.pagination.resultsPerPage}
                      totalNumber={allSpeciesPagination.pagination.totalNumber}
                      onChange={allSpeciesPagination.changeResultsPerPage}
                      dataTest="sites-items-per-page"
                    />
                    {allSpeciesPagination.pagination.totalPages > 1 && (
                      <Pagination
                        activePages={allSpeciesPagination.pagination.currentPage}
                        numberOfPages={allSpeciesPagination.pagination.totalPages}
                        nextPage={allSpeciesPagination.nextPage}
                        prevPage={allSpeciesPagination.prevPage}
                        nextSection={allSpeciesPagination.nextSection}
                        prevSection={allSpeciesPagination.prevSection}
                        selectPage={allSpeciesPagination.selectPage}
                        dataTest="page-number"
                      />
                    )}
                  </SpeciesPaginationWrapper>
                </LoadingWrapper>
              </Col>
            )}
            {renderSpeciesListToConfirmRejectedBySecretariat.length > 0 && (
              <Col md={8} mdOffset={2}>
                <Row>
                  <Col md={8} mdOffset={2}>
                    <SpeciesListTitleWrapper>
                      <SpeciesListTitle marginTop="48px">
                        <FormattedMessage id="rejectedTriggers" />
                      </SpeciesListTitle>
                    </SpeciesListTitleWrapper>
                  </Col>
                </Row>
                <LoadingWrapper size={"24px"} isLoading={isFilesLoading || isLoading}>
                  {renderSpeciesListToConfirmRejectedBySecretariat}
                </LoadingWrapper>
              </Col>
            )}
          </Row>
        )}
        {showUploader && (
          <Row>
            <Col md={8} mdOffset={2} style={{ marginTop: "32px" }}>
              <SpeciesListTitleWrapper>
                <SpeciesListTitle></SpeciesListTitle>
              </SpeciesListTitleWrapper>

              <UploadSiteFiles
                label="uploadSiteFiles"
                field="uploadSiteFiles"
                infoTooltip="speciesUploaderHint"
                height="128px"
                dataTest="uploadSiteFiles-text-area"
                isOptional={true}
                disabled={disabled}
              />

              <Uploader
                key={`${siteId}-${historyId ?? "assessment"}-${uploadedFiles?.length}-${uploadedFiles
                  ?.map((f) => f.id || f.blob_name || f.file_name || f.name)
                  .join("|")}`}
                params={uploaderParams}
                // Pass SAS-first URLs so <a href> uses exact fileUrl
                siteFiles={uploaderFiles}
                disabled={disabled}
                fileType="species"
                fetchFilesFn={async (siteId, siteHistoryId) => {
                  try {
                    let res
                    if (isReassessment) {
                      try {
                        res = await requestGetSpeciesFiles({ id: siteId, siteHistoryId: siteHistoryId })
                      } catch {
                        res = await requestGetSpeciesFiles(siteId, siteHistoryId)
                      }
                    } else {
                      try {
                        res = await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        res = await requestGetSpeciesFiles(siteId)
                      }
                    }
                    return res
                  } catch {
                    return { files: [] }
                  }
                }}
                uploadFn={async (siteId, siteHistoryId, formData, options) => {
                  try {
                    const response = await requestUpdateSpeciesFiles(siteId, siteHistoryId, formData, options)
                    
                    let nextFiles = extractFiles(response).map(normFile)
                    if (!nextFiles.length) {
                      let res
                      try {
                        res = isReassessment
                          ? await requestGetSpeciesFiles({ id: siteId, siteHistoryId: historyId })
                          : await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        res = isReassessment
                          ? await requestGetSpeciesFiles(siteId, historyId)
                          : await requestGetSpeciesFiles(siteId)
                      }
                      nextFiles = extractFiles(res).map(normFile)
                    }
                    const merged = uniqByKey(nextFiles)
                    setUploadedFiles(merged)
                    // Don't update site context with species files - they should be separate
                    return response
                  } catch (error) {
                    showErrorToast(<FormattedMessage id="uploadFailed" />)
                    throw error
                  }
                }}
                deleteFn={async (...args) => {
                  try {
                    const response = await requestDeleteSpeciesFiles(...args)
                    let nextFiles = extractFiles(response).map(normFile)
                    if (!nextFiles.length) {
                      let res
                      try {
                        res = isReassessment
                          ? await requestGetSpeciesFiles({ id: siteId, siteHistoryId: historyId })
                          : await requestGetSpeciesFiles({ id: siteId })
                      } catch {
                        res = isReassessment
                          ? await requestGetSpeciesFiles(siteId, historyId)
                          : await requestGetSpeciesFiles(siteId)
                      }
                      nextFiles = extractFiles(res).map(normFile)
                    }
                    const merged = uniqByKey(nextFiles)
                    setUploadedFiles(merged)
                    // Don't update site context with species files - they should be separate
                    return response
                  } catch (error) {
                    showErrorToast(<FormattedMessage id="deleteFailed" />)
                    throw error
                  }
                }}
              />

              <div style={{ marginTop: "8px", fontSize: "14px" }} />
            </Col>
          </Row>
        )}
      </Container>

      <Modal
        speciesToReassess={speciesToWorkWith}
        isOpen={isSpeciesDetailsModalOpen}
        toggleModal={closeEditSpeciesModal}
        width="1088px"
        modalTitleId="speciesAssessment"
        padding="0px"
        bigModal={true}
        autoSaveIcon={true}
        isDraft={site.SiteStatusId === SITE_STATUS_DRAFT_ID}
        cancelHandler={
          speciesToWorkWith.lastReassessmentDate && isNcgOrIndividualUser && site.SiteStatusId === SITE_STATUS_DRAFT_ID
            ? () => setIsOpen(true)
            : false
        }
      >
        <SpeciesDetailsModal
          closeEditSpeciesModal={closeEditSpeciesModal}
          openCancelModal={() => setIsOpen(true)}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          item={speciesToWorkWith}
        />
      </Modal>

      <Modal
        width={"416px"}
        isOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        modalTitleId="removeSpecies"
        bottomBorder={true}
      >
        <RemoveSpeciesModal
          itemName={speciesToWorkWith.scientificName}
          item={speciesToWorkWith}
          toggleModal={setIsModalOpen}
          confirm={removeSpeciesFromList}
        />
      </Modal>
    </SpeciesDetailsSearchFormWrapper>
  )
}

export default SpeciesDetailsSearchForm
